// File: frontend/src/router.jsx
import React, { Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';

// --- Layouts ---
import MainAppLayout from './layouts/MainAppLayout';
import AuthLayout from './layouts/AuthLayout';
import ReelsLayout from './layouts/ReelsLayout';

// --- Loader for lazy routes ---
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '1.5em',
    backgroundColor: 'var(--color-background-primary)',
    color: 'var(--color-text-primary)'
  }}>
    Loading...
  </div>
);

// --- CRITICAL AUTH PAGES (NOT LAZY) ---
import GoogleCallbackPage from './pages/GoogleCallbackPage';
import OnboardingPage from './pages/OnboardingPage';

// --- MODE COMPONENTS (13 Modes) ---
const EventsMode = React.lazy(() => import('./components/events/App'));
const TravelMode = React.lazy(() => import('./components/Travel/TravelApp'));
const DatingMode = React.lazy(() => import('./components/dating/App'));
const BookingMode = React.lazy(() => import('./components/booking/BookingPlatform'));
const ShoppingMode = React.lazy(() => import('./components/Shopping/ShoppingMode'));
const NewsMode = React.lazy(() => import('./components/News/NewsMode'));
const CommunityMode = React.lazy(() => import('./components/communities/CommunitiesApp'));
const VolunteeringMode = React.lazy(() => import('./components/Volunteering/VolunteeringMode'));
const TaxiMode = React.lazy(() => import('./components/uber-clone/App'));
const FoodMode = React.lazy(() => import('./components/food-delivery/FoodMode'));
const HiringMode = React.lazy(() => import('./components/hiring-platform/HiringMode'));
const VideoMode = React.lazy(() => import('./components/video/VideoMode'));
const SpotifyMode = React.lazy(() => import('./components/Spotify/SoundWaveApp'));

// --- Core Pages (lazy) ---
const HomePage = React.lazy(() => import('./pages/HomePage'));
const CategoryPage = React.lazy(() => import('./pages/CategoryPage'));
const PostDetailPage = React.lazy(() => import('./pages/PostDetailPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const ExplorePage = React.lazy(() => import('./pages/ExplorePage'));
const NotificationsPage = React.lazy(() => import('./pages/NotificationsPage'));
const SearchResultsPage = React.lazy(() => import('./pages/SearchResultsPage'));
const UserListPage = React.lazy(() => import('./pages/UserListPage'));
const AdminPage = React.lazy(() => import('./pages/AdminPage'));
const MeetingDetailPage = React.lazy(() => import('./pages/MeetingDetailPage'));

// --- Chat Pages ---
const ChatListPage = React.lazy(() => import('./features/chat/pages/ChatListPage'));
const ChatPage = React.lazy(() => import('./features/chat/pages/ChatWindowPage'));
const NewChatPage = React.lazy(() => import('./features/chat/pages/NewChatPage'));

// --- Auth Pages ---
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

// --- Feature Pages ---
const EventsMapPage = React.lazy(() => import('./pages/EventsMapPage'));
const EventDetailPage = React.lazy(() => import('./pages/EventDetailPage'));
const ShopPage = React.lazy(() => import('./pages/ShopPage'));
const ReelsPage = React.lazy(() => import('./pages/ReelsPage'));
const CreateNewContentPage = React.lazy(() => import('./pages/CreateNewContentPage'));
const CreateVideoPage = React.lazy(() => import('./pages/CreateVideoPage'));
// --- Share Functionality ---
const ShareSelectionPage = React.lazy(() => import('./pages/ShareSelectionPage'));
const CreateResourcePage = React.lazy(() => import('./pages/CreateResourcePage'));

// --- Form Pages for Content Creation ---
const CreateEventForm = React.lazy(() => import('./components/create/CreateEventForm'));
const CreateEventLocationForm = React.lazy(() => import('./components/create/CreateEventLocationForm'));
const CreateEventPreview = React.lazy(() => import('./components/create/CreateEventPreview'));
const CreateEventReviewForm = React.lazy(() => import('./components/create/CreateEventReviewForm'));
const CreateTaskForm = React.lazy(() => import('./components/create/CreateTaskForm'));
// --- Community Creation Form ---
const CreateCommunityForm = React.lazy(() => import('./components/community/CreateCommunityForm'));
// --- Job Creation Form ---
const JobCreationForm = React.lazy(() => import('./components/job/JobCreationForm'));
const JobApplications = React.lazy(() => import('./components/job/JobApplications'));
// --- Event Success Page ---
const EventCreatedSuccessPage = React.lazy(() => import('./pages/EventCreatedSuccessPage'));

// --- Utility Pages ---
const HelpCenterPage = React.lazy(() => import('./pages/HelpCenterPage'));
const SavedPostsPage = React.lazy(() => import('./pages/SavedPostsPage'));
const LogoutPage = React.lazy(() => import('./pages/LogoutPage'));

// --- WanderMatch Wrapper (replaces More page) ---
const WanderMatchWrapper = React.lazy(() => import('./components/Home/HomeScreen'));
const TaskDetailPage = React.lazy(() => import('./pages/TaskDetailPage'));
const ItemDetailPage = React.lazy(() => import('./pages/ItemDetailPage')); // ✅ ADD THIS
const LocationDetailsPage = React.lazy(() => import('./pages/LocationDetailsPage')); // ✅ NEW
const CreateFundraiserForm = React.lazy(() => import('./components/fundraiser/CreateFundraiserForm'));
const CreateMarketplaceListingForm = React.lazy(() => import('./components/marketplace/CreateMarketplaceListingForm'));
const CreateServiceForm = React.lazy(() => import('./components/services/CreateServiceForm'));
const CreateNewsForm = React.lazy(() => import('./components/news/CreateNewsForm'));
const CreateRideForm = React.lazy(() => import('./components/rides/CreateRideForm'));
const CreateFoodOrderForm = React.lazy(() => import('./components/food/CreateFoodOrderForm'));
const CreateDatingProfileForm = React.lazy(() => import('./components/datings/CreateDatingProfileForm'));
const DatingProfileDetailPage = React.lazy(() => import('./components/datings/DatingProfileDetailSheet'));
const CreateMusicForm = React.lazy(() => import('./components/music/CreateMusicForm'));
// --- Placeholder Components for Resource Pages ---
const ResourcesPlaceholder = () => (
  <div style={{ padding: '2rem', textAlign: 'center' }}>
    <h2>Resources</h2>
    <p>Browse and share resources with your neighbors.</p>
    <p style={{ marginTop: '1rem', color: '#666' }}>
      This feature is coming soon!
    </p>
  </div>
);

const ResourceDetailPlaceholder = () => (
  <div style={{ padding: '2rem', textAlign: 'center' }}>
    <h2>Resource Details</h2>
    <p>View detailed information about this resource.</p>
    <p style={{ marginTop: '1rem', color: '#666' }}>
      This page is under construction.
    </p>
  </div>
);

const UserResourcesPlaceholder = () => (
  <div style={{ padding: '2rem', textAlign: 'center' }}>
    <h2>My Resources</h2>
    <p>Manage your shared resources and exchange requests.</p>
    <p style={{ marginTop: '1rem', color: '#666' }}>
      Coming soon!
    </p>
  </div>
);

const VerifyEmailPlaceholder = () => (
  <div style={{ padding: '2rem', textAlign: 'center' }}>
    <h2>Email Verification</h2>
    <p>Please check your email to verify your account.</p>
  </div>
);

// For now, use EventsMapPage as ResourcesPage if it exists
const ResourcesPage = EventsMapPage || ResourcesPlaceholder;
const UserResourcesPage = UserResourcesPlaceholder;
const VerifyEmailPage = VerifyEmailPlaceholder;

// Path constants
export const PATHS = {
  // Core paths
  HOME: '/',
  LOGIN: '/auth/login',
  REGISTER: '/auth/register',
  PROFILE: (username) => `/profile/${username}`,
  
  // Auth callback
  GOOGLE_CALLBACK: '/auth/google/callback',
  TASK_DETAIL: (id) => `/task/${id}`,  // ✅ ADD THIS LINE

  ONBOARDING: '/onboarding',
  POST_JOB: '/post-job',

  
  // Mode paths
  EVENTS: '/events',
  MEETING_DETAIL: (id) => `/meeting/${id}`,
  EXPLORE_EVENTS: '/explore-events',
  TRAVEL: '/travel',
  DATING: '/dating',
  BOOKING: '/booking',
  SHOPPING: '/shopping',
  NEWS: '/news',
  COMMUNITIES: '/communities',
  VOLUNTEERING: '/volunteering',
  TAXI: '/taxi',
  FOOD: '/food',
  HIRING: '/hiring',
  VIDEO: '/video',
  MUSIC: '/music',
  
  // Chat paths
  CHAT_LIST: '/chat',
  CHAT_CONVERSATION: (id) => `/chat/${id}`,
  NEW_CHAT: '/chat/new',
  NEW_CHAT_WITH_USER: (userId) => `/chat/new/${userId}`,
  
  // Main features
  NOTIFICATIONS: '/notifications',
  EXPLORE: '/explore',
  EVENTS_MAP: '/events-map',
  EVENT_DETAIL: (id) => `/event/${id}`,
  VIDEO_REELS: '/reels',
  VIDEO_REELS_FULLSCREEN: '/reels-fullscreen',
  CREATE: '/create',
  CREATE_POST: '/create',
  CREATE_VIDEO: '/create-video',

  SHOP: '/shop',
  MORE: '/more',
  
  // WanderMatch paths
  WANDERMATCH: '/wandermatch',
  WANDERMATCH_PROFILE: '/wandermatch/profile',
  WANDERMATCH_MATCHES: '/wandermatch/matches',
  WANDERMATCH_TRIPS: '/wandermatch/trips',
  WANDERMATCH_EXPLORE: '/wandermatch/explore',
  
  // Share functionality
  SHARE_SELECTION: '/share/select',
  CREATE_RESOURCE: '/resources/create',
  RESOURCE_CREATE: '/resources/create',
  JOB_APPLICATIONS: (jobId) => `/jobs/${jobId}/applications`,
  
  // Content creation paths
  CREATE_EVENT: '/create-event',
  CREATE_EVENT_LOCATION_FORM: '/create-event/location',
  CREATE_EVENT_PREVIEW: '/create-event/preview',
  CREATE_EVENT_REVIEW_FORM: '/create-event/review',      
  EVENT_CREATED_SUCCESS: '/event-created-success',
  CREATE_TASK: '/create-task',
  CREATE_EVENT_FORM: '/create-event',
  CREATE_TASK_FORM: '/create-task',
  CREATE_COMMUNITY: '/create-community',
  LIST_ITEM: '/list-item',
  OFFER_SERVICE: '/offer-service',
  CREATE_NEWS: '/create-news',
  REQUEST_RIDE: '/request-ride',
  ORDER_FOOD: '/order-food',
  DATING_PROFILE: '/dating-profile',
  DATING_PROFILE_DETAIL: (id) => `/dating-profile/${id}`,
  SHARE_MUSIC: '/share-music',


  
  // Resource sharing paths
  RESOURCES: '/resources',
  RESOURCES_MAP: '/resources/map',
  RESOURCE_DETAIL: (id) => `/resources/${id}`,
  RESOURCE_EDIT: (id) => `/resources/${id}/edit`,
  MY_RESOURCES: '/my-resources',
  RESOURCE_REQUESTS: (id) => `/resources/${id}/requests`,
  ITEM_DETAIL: (id) => `/item/${id}`,  // ✅ Path helper for /item/:id
  LOCATION_DETAIL: (name) => `/location/${encodeURIComponent(name)}`, // ✅ NEW

  
  // Utility paths
  HELP_CENTER: '/help-center',
  SAVED_POSTS: '/saved-posts',
  LOGOUT: '/logout',
  SETTINGS: '/settings',
  
  // Content paths
  CATEGORY: (slug) => `/category/${slug}`,
  POST_DETAIL: (id) => `/post/${id}`,
  SEARCH_RESULTS: '/search',
  
  // Admin
  ADMIN_DASHBOARD: '/admin',
  
  // User lists
  USER_FOLLOWERS: (username) => `/users/${username}/followers`,
  USER_FOLLOWING: (username) => `/users/${username}/following`,
  
  // Email verification
  VERIFY_EMAIL: (token) => `/verify-email/${token}`,
};

export const router = createBrowserRouter([
  // ===============================================
  // CRITICAL: GOOGLE OAUTH CALLBACK - MUST BE FIRST!
  // ===============================================
  {
    path: '/auth/google/callback',
    element: <GoogleCallbackPage />
  },
  
  // ===============================================
  // CRITICAL: ONBOARDING - MUST BE SECOND!
  // ===============================================
  {
    path: '/onboarding',
    element: <OnboardingPage />
  },
  
  // ===============================================
  // MODE ROUTES - HIGHEST PRIORITY, TOP-LEVEL
  // These work regardless of authentication status
  // ===============================================
  
  // Events Mode
  { path: '/events', element: <EventsMode /> },
  { path: '/events/*', element: <EventsMode /> },
  { path: '/explore-events', element: <EventsMode /> },
  { path: '/explore-events/*', element: <EventsMode /> },
  { path: '/events-experience', element: <Navigate to="/events" replace /> },
  
  // Travel Mode
  { path: '/travel', element: <TravelMode /> },
  { path: '/travel/*', element: <TravelMode /> },
  
  // Dating Mode
  { path: '/dating', element: <DatingMode /> },
  { path: '/dating/*', element: <DatingMode /> },
  
  // Booking Mode
  { path: '/booking', element: <BookingMode /> },
  { path: '/booking/*', element: <BookingMode /> },
  
  // Shopping Mode
  { path: '/shopping', element: <ShoppingMode /> },
  { path: '/shopping/*', element: <ShoppingMode /> },
  
  // News Mode
  { path: '/news', element: <NewsMode /> },
  { path: '/news/*', element: <NewsMode /> },
  
  // Communities Mode
  { path: '/communities', element: <CommunityMode /> },
  { path: '/communities/*', element: <CommunityMode /> },
  
  // Volunteering Mode
  { path: '/volunteering', element: <VolunteeringMode /> },
  { path: '/volunteering/*', element: <VolunteeringMode /> },
  
  // Taxi Mode
  { path: '/taxi', element: <TaxiMode /> },
  { path: '/taxi/*', element: <TaxiMode /> },
  
  // Food Delivery Mode
  { path: '/food', element: <FoodMode /> },
  { path: '/food/*', element: <FoodMode /> },
  
  // Hiring Mode
  { path: '/hiring', element: <HiringMode /> },
  { path: '/hiring/*', element: <HiringMode /> },
  
  // Video Mode
  { path: '/video', element: <VideoMode /> },
  { path: '/video/*', element: <VideoMode /> },
  
  // Music/Spotify Mode
  { path: '/music', element: <SpotifyMode /> },
  { path: '/music/*', element: <SpotifyMode /> },
  
  // ===============================================
  // MAIN APP ROUTES (AUTHENTICATED)
  // ===============================================
  {
    path: '/',
    element: <MainAppLayout />,
    children: [
      // Core routes
      { index: true, element: <HomePage /> },
      { path: 'task/:taskId', element: <TaskDetailPage /> }, // ✅ ADD THIS LINE

      { path: 'explore', element: <ExplorePage /> },
      { path: 'jobs/:jobId/applications', element: <JobApplications /> }, // ← ADD THIS

      { path: 'events-map', element: <EventsMapPage /> },
      { path: 'event/:id', element: <EventDetailPage /> },
      { path: 'meeting/:meetingId', element: <MeetingDetailPage /> },
      { path: 'location/:locationName', element: <LocationDetailsPage /> }, // ✅ NEW ROUTE
      
      { path: 'shop', element: <ShopPage /> },
      { path: 'reels', element: <ReelsPage /> },
      { path: 'more', element: <WanderMatchWrapper /> },
      { path: 'create', element: <CreateNewContentPage /> },
      { path: 'create-video', element: <CreateVideoPage /> },
      // WanderMatch routes
      { path: 'wandermatch', element: <WanderMatchWrapper /> },
      { path: 'wandermatch/profile', element: <WanderMatchWrapper /> },
      { path: 'wandermatch/matches', element: <WanderMatchWrapper /> },
      { path: 'wandermatch/trips', element: <WanderMatchWrapper /> },
      { path: 'wandermatch/explore', element: <WanderMatchWrapper /> },
      
      // Share functionality route
      { path: 'share/select', element: <ShareSelectionPage /> },
      
      // Content creation routes
      { path: 'create-event', element: <CreateEventForm /> },
      { path: 'create-event/location', element: <CreateEventLocationForm /> },
      { path: 'create-event/preview', element: <CreateEventPreview /> },
      { path: 'create-event/review', element: <CreateEventReviewForm /> },
      { path: 'event-created-success', element: <EventCreatedSuccessPage /> },
      { path: 'create-task', element: <CreateTaskForm /> },
      { path: 'post-job', element: <JobCreationForm /> }, // ✅ ADD THIS LINE
      { path: 'create-community', element: <CreateCommunityForm /> },
      // Content routes
      { path: 'category/:categorySlug', element: <CategoryPage /> },
      { path: 'post/:postId', element: <PostDetailPage /> },
      
      // Profile routes
      { path: 'profile/:username', element: <ProfilePage /> },
      { path: 'users/:username/followers', element: <UserListPage type="followers" /> },
      { path: 'users/:username/following', element: <UserListPage type="following" /> },
      
      // Feature routes
      { path: 'notifications', element: <NotificationsPage /> },
      { path: 'settings/*', element: <SettingsPage /> },
      { path: 'search', element: <SearchResultsPage /> },
      
      // Chat routes
      { path: 'chat', element: <ChatListPage /> },
      { path: 'chat/new', element: <NewChatPage /> },
      { path: 'chat/new/:userId', element: <NewChatPage /> },
      { path: 'chat/:conversationId', element: <ChatPage /> },
      
      // Resource sharing routes
      { path: 'resources', element: <ResourcesPage /> },
      { path: 'resources/map', element: <EventsMapPage /> },
      { path: 'resources/create', element: <CreateResourcePage /> },
      { path: 'resources/:id', element: <ItemDetailPage /> },  // ✅ Use real component
      { path: 'item/:id', element: <ItemDetailPage /> },       // ✅ Use real component
      { path: 'resources/:id/edit', element: <CreateResourcePage /> },
      { path: 'resources/:id/requests', element: <UserResourcesPage /> },
      { path: 'my-resources', element: <UserResourcesPage /> },

      
      // Utility routes
      { path: 'help-center', element: <HelpCenterPage /> },
      { path: 'saved-posts', element: <SavedPostsPage /> },
      { path: 'logout', element: <LogoutPage /> },
      { path: 'start-fundraiser', element: <CreateFundraiserForm /> },
      { path: 'list-item', element: <CreateMarketplaceListingForm /> },
      { path: 'offer-service', element: <CreateServiceForm /> },
      { path: 'create-news', element: <CreateNewsForm /> },
      { path: 'request-ride', element: <CreateRideForm /> },
      { path: 'order-food', element: <CreateFoodOrderForm /> },
      { path: 'dating-profile', element: <CreateDatingProfileForm /> },
      { path: 'dating-profile/:id', element: <DatingProfileDetailPage /> },
      { path: 'share-music', element: <CreateMusicForm /> },
      { path: 'admin/*', element: <AdminPage /> },
      
      // Messages redirects
      { path: 'messages', element: <Navigate to="/chat" replace /> },
      { path: 'messages/new', element: <Navigate to="/chat/new" replace /> },
      { path: 'messages/:conversationId', element: <Navigate to="/chat/:conversationId" replace /> },
      
      // Auth redirects
      { path: 'login', element: <Navigate to={PATHS.LOGIN} replace /> },
      { path: 'register', element: <Navigate to={PATHS.REGISTER} replace /> },
    ],
  },
  
  // Full-screen reels layout
  {
    path: 'reels-fullscreen',
    element: <ReelsLayout />,
    children: [
      { index: true, element: <ReelsPage /> }
    ],
  },
  
  // Auth layout
  {
    path: 'auth',
    element: <AuthLayout />,
    children: [
      { path: 'login', element: <LoginPage /> },
      { path: 'register', element: <RegisterPage /> },
      { index: true, element: <Navigate to="login" replace /> },
    ],
  },
  
  // Email verification
  {
    path: 'verify-email/:token',
    element: <VerifyEmailPage />
  },
  
  // 404 catch-all
  { path: '*', element: <NotFoundPage /> },
]);

// Main Router Component with Suspense
const AppRouter = () => (
  <Suspense fallback={<PageLoader />}>
    <RouterProvider router={router} />
  </Suspense>
);

export default AppRouter;
